import { ApiError } from '@iheartjane/data-access';
import type { Cart } from '@iheartjane/data-access';
import { cartMutationKeys, useCart } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { keepPreviousData, useIsMutating } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { selectCart } from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { selectPosCart } from '@jane/monolith/customer/redux/posCart';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { getJwt } from '@jane/shared/auth';
import { useEcommApp } from '@jane/shared/providers';
import type { Address } from '@jane/shared/types';

import {
  toDeliveryAddressParams,
  useLocalDeliveryAddress,
} from './deliveryAddress';
import { updateLocalCartUuid, useLocalCartUuid } from './localCartUuid';
import { mapV1CartToV3 } from './mapV1CartToV3';
import type { V1CartInput } from './mapV1CartToV3';

/**
 * The cart in the v3 `Cart` shape: from `GET /v3/carts/:uuid` flag-on, mapped
 * from the v1 slice flag-off.
 *
 * Don't enable the flag until every write has moved to v3: v1 writes only
 * update the slice, and v3 writes only update the query cache.
 * TODO(carts-v3): remove the flag-off path once v3 is the only source.
 */
export interface CartFacadeState {
  cart: Cart | null;
  /** Sum of product counts, not the number of lines. */
  cartSize: number;
  /** Not returned by the API: in the slice flag-off, in storage flag-on. */
  deliveryAddress: Address | null;
  /** The load has settled, even with no cart. */
  hasLoaded: boolean;
  hasSpecial: boolean;
  /**
   * Only `applyLoyalty`, not `isMutating`: it guards the reward buttons against
   * double-applying, and shouldn't block them during other cart writes.
   */
  isApplyingLoyalty: boolean;
  /**
   * Any `useUpdateCart` in flight (today only the mode change); drives the
   * totals shimmer. Don't filter it by `variables`, the payload shape.
   */
  isChangingReservationMode: boolean;
  isFetching: boolean;
  isLoading: boolean;
  /**
   * A cart write is in flight. Not every write is visible:
   * - Flag-off: `updateProduct`, `removeProduct`, `removeLoyalty` and
   *   `updateDeliveryAddress` set no slice flag.
   * - Flag-on: `deleteUnavailableProducts` skips the mutation cache. Checkout
   *   is still v1, so its slice flags are OR'd in.
   */
  isMutating: boolean;
  orderTotal: number;
}

export const useCartState = (): CartFacadeState => {
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  // Only used to adopt a legacy cart uuid
  const { partnerId: storeId } = useCustomerSelector(selectEmbeddedApp);
  // Skipped flag-off, where the slice is the source
  const localCartUuid = useLocalCartUuid(appMode, storeId, !!useV3Cart);

  const localDeliveryAddress = useLocalDeliveryAddress(!!useV3Cart);

  const {
    cart: v1Cart,
    deliveryAddress: v1DeliveryAddress,
    isLoading,
    hasLoaded,
    isAddingItemToCart,
    isApplyingCrmRedemption,
    isChangingReservationModes,
    isCheckingOut,
    isDeletingUnavailableProducts,
    isFixingUnavailableCheckout,
  } = useCustomerSelector(selectCart);

  // Without the address the delivery fee comes back as 0.
  const deliveryAddress = useV3Cart
    ? toDeliveryAddressParams(localDeliveryAddress)
    : undefined;

  const v3Cart = useCart(
    {
      appMode,
      deliveryAddress,
      jwt: useV3Cart ? getJwt() : null,
      uuid: localCartUuid ?? '',
    },
    // Keep the old cart on screen while an address change refetches. Set here,
    // not in `cart.queries.ts`, because kiosk shares that hook.
    { enabled: !!useV3Cart, placeholderData: keepPreviousData }
  );

  // A 404 means the cart was destroyed (last product, checkout, expiry). Drop
  // the uuid so consumers see an empty bag; `removeQueries` would refetch.
  const cartGone =
    !!useV3Cart &&
    !!localCartUuid &&
    v3Cart.error instanceof ApiError &&
    v3Cart.error.response.status === 404;

  useEffect(() => {
    if (cartGone) updateLocalCartUuid(appMode, storeId, null);
  }, [appMode, cartGone, storeId]);

  // Flag-off only: v1 keeps POS totals on their own slice; v3 includes them.
  const posCartTotals =
    useCustomerSelector(selectPosCart).posCart.totals ?? null;

  // `isPending` is per hook instance, so read in-flight writes from the
  // mutation cache. Prefix match: `all` counts every cart mutation. 0 flag-off,
  // as long as `useCartActions` is the only cart-mutation caller here.
  const cartMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.all,
  });
  const addLoyaltyMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.addLoyaltyRedemption,
  });
  const updateCartMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.update,
  });

  // No cart = empty uuid. The cast drops `DeepReadonly` (the mapper doesn't
  // mutate) and matches the slice's runtime shape, which `V1CartInput` models.
  const mappedV1Cart = useMemo(
    () =>
      !useV3Cart && v1Cart.uuid
        ? mapV1CartToV3(v1Cart as unknown as V1CartInput, posCartTotals)
        : null,
    [useV3Cart, v1Cart, posCartTotals]
  );

  const cart = useV3Cart ? (v3Cart.data ?? null) : mappedV1Cart;

  const cartSize = cart
    ? cart.products.reduce((total, product) => total + product.count, 0)
    : 0;

  const hasSpecial = Boolean(
    cart &&
    (cart.cart_discount_special_id != null ||
      cart.group_special_id != null ||
      cart.brand_discounts.length > 0 ||
      cart.products.some((product) => product.special_id != null))
  );

  return {
    cart,
    // With no uuid the query never runs, so "settled" is immediate.
    hasLoaded: useV3Cart ? !localCartUuid || v3Cart.isFetched : hasLoaded,
    isLoading: useV3Cart ? v3Cart.isLoading : isLoading && !hasLoaded,
    isFetching: useV3Cart ? v3Cart.isFetching : isLoading,
    // Drop the v1 flags from the flag-on branch once checkout moves to v3.
    isMutating: useV3Cart
      ? cartMutationCount > 0 || isCheckingOut || isFixingUnavailableCheckout
      : isAddingItemToCart ||
        isApplyingCrmRedemption ||
        isChangingReservationModes ||
        isCheckingOut ||
        isDeletingUnavailableProducts ||
        isFixingUnavailableCheckout,
    isApplyingLoyalty: useV3Cart
      ? addLoyaltyMutationCount > 0
      : isApplyingCrmRedemption,
    isChangingReservationMode: useV3Cart
      ? updateCartMutationCount > 0
      : isChangingReservationModes,
    cartSize,
    deliveryAddress: useV3Cart
      ? localDeliveryAddress
      : (v1DeliveryAddress ?? null),
    hasSpecial,
    orderTotal: cart?.order_total ?? 0,
  };
};
